/** Default social share image (absolute URL for crawlers). */
export const DEFAULT_OG_IMAGE_URL = 'https://primedocumentsign.com/preview.webp'

const SITE_TITLE_SUFFIX = ' | Prime Document Sign'

/**
 * Trim document title for SERP (~60 chars); avoids truncation mid-word when possible.
 * @param {string} s
 * @param {number} [max]
 */
export function clampDocumentTitle(s, max = 60) {
  if (s == null || typeof s !== 'string') return s
  const t = s.replace(/\s+/g, ' ').trim()
  if (t.length <= max) return t
  const cut = t.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  const head = lastSpace > 36 ? cut.slice(0, lastSpace) : cut
  return `${head.trimEnd().replace(/[\s, , :|,;]+$/, '')}…`
}

/** Trim meta description to a SERP-friendly length (word boundary). */
export function clampMetaDescription(s, max = 158) {
  if (s == null || typeof s !== 'string') return s
  const t = s.replace(/\s+/g, ' ').trim()
  if (t.length <= max) return t
  const cut = t.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  const head = lastSpace > 48 ? cut.slice(0, lastSpace) : cut
  return `${head.trimEnd()}…`
}

/**
 * Build a single-line title with brand suffix, avoiding duplicate brand and extreme length.
 * @param {string} primary ,  page-specific title (may already include brand)
 */
export function formatBlogMetaTitle(primary) {
  if (!primary || typeof primary !== 'string') return primary
  let base = primary.replace(/\s*\|\s*Prime Document Sign\s*$/i, '').trim()
  const maxTotal = 58
  const suffix = SITE_TITLE_SUFFIX
  if (base.length + suffix.length <= maxTotal) return base + suffix
  const avail = maxTotal - suffix.length - 1
  if (avail < 16) return `Blog${suffix}`
  const trimmed = base.slice(0, avail).trimEnd().replace(/[\s,;, , :]+$/, '')
  return `${trimmed}…${suffix}`
}

/**
 * Normalize pathname for canonical URLs (no trailing slash except root; /index.html → /).
 */
export function normalizePublicPathname(pathname) {
  if (!pathname || typeof pathname !== 'string') return '/'
  let p = pathname.trim()
  if (!p.startsWith('/')) p = `/${p}`
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
  if (p === '/index.html') p = '/'
  return p || '/'
}

/**
 * Self-referencing canonical for the URL actually loaded (path only; query/hash omitted).
 * Use this so /pt/… and /es/… are not declared alternates of /.
 */
export function buildSelfCanonicalUrl(origin, pathname) {
  const base = (origin || '').replace(/\/$/, '')
  const p = normalizePublicPathname(pathname)
  const pathPart = p === '/' ? '/' : p
  return `${base}${pathPart}`
}

const SOFTWARE_ENTITY_ID = 'https://primedocumentsign.com/#software'
const WEBSITE_ENTITY_ID = 'https://primedocumentsign.com/#website'

/**
 * Pricing page: WebPage + ItemList of Offer nodes referencing the sitewide SoftwareApplication @id.
 * Uses live tier prices from the app so schema matches what users see.
 */
export function buildPricingOffersJsonLd(tierPricing, canonicalPricingUrl) {
  if (!canonicalPricingUrl || !tierPricing) return ''
  const fmt = (n) => (typeof n === 'number' && Number.isFinite(n) ? String(n) : '0')
  const offers = [
    { name: 'Basic ,  monthly', price: fmt(tierPricing.basic?.monthly), priceCurrency: 'USD' },
    { name: 'Standard ,  monthly', price: fmt(tierPricing.standard?.monthly), priceCurrency: 'USD' },
    { name: 'Premium ,  monthly', price: fmt(tierPricing.premium?.monthly), priceCurrency: 'USD' },
    { name: '7-day free trial', price: '0', priceCurrency: 'USD' },
  ]
  const graph = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonicalPricingUrl}#webpage`,
        url: canonicalPricingUrl,
        name: 'Pricing ,  Prime Document Sign',
        isPartOf: { '@id': WEBSITE_ENTITY_ID },
        about: { '@id': SOFTWARE_ENTITY_ID },
      },
      {
        '@type': 'ItemList',
        name: 'Prime Document Sign ,  published prices',
        numberOfItems: offers.length,
        itemListElement: offers.map((o, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Offer',
            name: o.name,
            price: o.price,
            priceCurrency: o.priceCurrency,
            availability: 'https://schema.org/InStock',
            itemOffered: { '@id': SOFTWARE_ENTITY_ID },
          },
        })),
      },
    ],
  }
  return JSON.stringify(graph)
}

/**
 * Client-side SEO head updates (SPA). Keeps <title>, meta description,
 * Open Graph, and Twitter Card tags aligned for crawlers and social previews.
 * @param {object} opts
 * @param {string} [opts.ogTitle] ,  Open Graph / Twitter title when different from `title` (e.g. shorter home headline).
 * @param {string} [opts.ogDescription] ,  OG / Twitter description when different from `meta[name=description]` (e.g. shorter social blurb).
 */
export function setDocumentSeo({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImageUrl = DEFAULT_OG_IMAGE_URL,
  robots,
}) {
  if (typeof document === 'undefined') return
  const head = document.head
  if (!head) return

  const descClamped = description != null && description !== '' ? clampMetaDescription(description) : description
  const titleClamped = title ? clampDocumentTitle(title) : ''

  if (titleClamped) document.title = titleClamped

  const setMetaContent = (selector, content) => {
    if (content == null || content === '') return
    const el = head.querySelector(selector)
    if (el) el.setAttribute('content', content)
  }

  if (robots != null && robots !== '') {
    setMetaContent('meta[name="robots"]', robots)
  }

  const rawSocialTitle = ogTitle || title || ''
  const socialTitle = rawSocialTitle ? clampDocumentTitle(rawSocialTitle) : titleClamped
  const socialDescription = ogDescription ? clampMetaDescription(ogDescription) : descClamped

  setMetaContent('meta[name="description"]', descClamped)
  setMetaContent('meta[property="og:title"]', socialTitle)
  setMetaContent('meta[property="og:description"]', socialDescription)
  setMetaContent('meta[name="twitter:title"]', socialTitle)
  setMetaContent('meta[name="twitter:description"]', socialDescription || descClamped)

  if (ogImageUrl) {
    setMetaContent('meta[property="og:image"]', ogImageUrl)
    setMetaContent('meta[property="og:image:secure_url"]', ogImageUrl)
    setMetaContent('meta[name="twitter:image"]', ogImageUrl)
  }

  if (canonicalUrl) {
    let link = head.querySelector('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      head.appendChild(link)
    }
    link.setAttribute('href', canonicalUrl)
    setMetaContent('meta[property="og:url"]', canonicalUrl)
  }
}
